import { useTranslation } from 'react-i18next'

import { Box, Text } from '@chakra-ui/react'

import { FacilityOptionWithAddressI } from '@/utils/forms/optionBuilders'

interface FacilityOptionLabelIProps {
  option: FacilityOptionWithAddressI
  isValueContext?: boolean
  /**
   * Defaults to 'light' — every current caller renders this inside the
   * legacy V1 `InputSelect`, which hardcodes a white background
   * (`colourStyles.ts`) regardless of dark mode, so a dark-mode-appropriate
   * (near-white) text color would render unreadably faint there. Pass
   * 'dark' explicitly when reusing this component on a surface that's
   * actually dark (e.g. inside a `DarkProvider`-wrapped select).
   */
  colorScheme?: 'light' | 'dark'
}

/**
 * Renders a facility select option's name, short-name/address subtitle, and
 * (when the result only matched on its address) a "matched by address" cue —
 * so operators can tell why a facility they didn't search by name appeared.
 * Pass as react-select's `formatOptionLabel` on facility selectors.
 */
export const FacilityOptionLabel = ({
  option,
  isValueContext = false,
  colorScheme = 'light',
}: FacilityOptionLabelIProps) => {
  const { t } = useTranslation()

  if (isValueContext) {
    return <Text isTruncated>{option.label}</Text>
  }

  return (
    <Box>
      <Text color={`text.primary.${colorScheme}`} fontSize='14px'>
        {option.label}
      </Text>
      {option.subtitle && (
        <Text color={`text.secondary.${colorScheme}`} fontSize='12px'>
          {option.subtitle}
        </Text>
      )}
      {option.matchedField === 'address' && (
        <Text
          color={`text.tertiary.${colorScheme}`}
          fontSize='11px'
          fontStyle='italic'
        >
          {t('common:facility_matched_by_address')}
        </Text>
      )}
    </Box>
  )
}
